import {
  ToastProvider as TamaguiToastProvider,
  Toast,
  ToastViewport,
  useToastController,
  useToastState,
} from '@tamagui/toast';
import React, { useEffect, useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getTokens } from 'tamagui';
import { TOAST_AUTO_DISMISS_MS } from './toast.constants';
import { registerToastHandler } from './toast.service';
import type { ToastType } from './toast.types';

interface ToastProviderProps {
  children: React.ReactNode;
}

interface ToastStyleConfig {
  backgroundColor: string;
  borderColor: string;
  titleColor: string;
  descriptionColor: string;
}

const toastStyles: Record<ToastType, ToastStyleConfig> = {
  success: {
    backgroundColor: '$successBg',
    borderColor: '$success',
    titleColor: '$successFont',
    descriptionColor: '$successFont',
  },
  error: {
    backgroundColor: '$errorBg',
    borderColor: '$error',
    titleColor: '$errorFont',
    descriptionColor: '$errorFont',
  },
};

const ToastHost = (): React.ReactNode => {
  const toast = useToastState();
  const toastController = useToastController();
  const insets = useSafeAreaInsets();
  const uiTokens = getTokens();

  useEffect(() => {
    const unregister = registerToastHandler((payload) => {
      if (!toastController?.show) {
        return;
      }

      toastController.show(payload.title, {
        message: payload.description,
        duration: payload.duration,
        native: false,
        customData: {
          variant: payload.type,
        },
      });
    });

    return unregister;
  }, [toastController]);

  const variant = useMemo<ToastType>(() => {
    if (!toast) {
      return 'success';
    }

    const customVariant =
      toast.customData?.variant ?? toast.variant ?? toast.preset;

    return customVariant === 'error' ? 'error' : 'success';
  }, [toast]);

  const toastStyle = toastStyles[variant];

  return (
    <>
      <ToastViewport
        top={insets.top + uiTokens.space.$true.val}
        left={uiTokens.space.$true.val}
        right={uiTokens.space.$true.val}
        pointerEvents="box-none"
      />

      {toast && (
        <Toast
          key={toast.id}
          duration={toast.duration ?? TOAST_AUTO_DISMISS_MS}
          backgroundColor={toastStyle.backgroundColor}
          borderColor={toastStyle.borderColor}
          borderWidth={1}
          padding="$true"
          elevation={1}
          onOpenChange={(open) => {
            if (!open) {
              toastController.hide();
            }
          }}
        >
          <Toast.Title fontFamily="$bold" color={toastStyle.titleColor}>
            {toast.title}
          </Toast.Title>
          {toast.message && (
            <Toast.Description size="$2" color={toastStyle.descriptionColor}>
              {toast.message}
            </Toast.Description>
          )}
        </Toast>
      )}
    </>
  );
};

export const ToastProvider = ({
  children,
}: ToastProviderProps): React.ReactNode => (
  <TamaguiToastProvider duration={TOAST_AUTO_DISMISS_MS} native={false}>
    {children}
    <ToastHost />
  </TamaguiToastProvider>
);
